import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCookieConsent } from '../../context/CookieConsentContext';

/**
 * GDPR/CCPA-compliant cookie consent banner.
 *
 * - Shows on every visit until the user makes a choice.
 * - Offers Accept All, Reject All, and Customize.
 * - Customize opens granular per-category toggles.
 * - "Necessary" is always on and cannot be disabled.
 * - Preferences are stored in localStorage (not in a cookie,
 *   avoiding the irony of needing a cookie to track cookie consent).
 */
const CookieConsentBanner: React.FC = () => {
  const { consentGiven, acceptAll, rejectAll, savePreferences } =
    useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (consentGiven) return null;

  const handleSaveCustom = () => {
    savePreferences({ functional, analytics, marketing });
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 p-4"
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
    >
      <div className="container-app max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-neutral-200 p-6">
        {!showDetails ? (
          <>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="flex-1">
                <h2 className="font-heading font-bold text-neutral-900 mb-1">
                  We value your privacy
                </h2>
                <p className="text-sm text-neutral-600">
                  We use cookies to improve your experience, analyze site
                  traffic, and personalize content. You can accept all
                  cookies, reject non-essential ones, or customize your
                  preferences. Read our{' '}
                  <Link
                    to="/cookies"
                    className="text-primary-500 hover:text-primary-600 underline"
                  >
                    Cookie Policy
                  </Link>{' '}
                  for details.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <button
                  onClick={rejectAll}
                  className="btn-ghost text-sm px-4 py-2"
                >
                  Reject All
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className="btn-ghost text-sm px-4 py-2"
                >
                  Customize
                </button>
                <button
                  onClick={acceptAll}
                  className="btn-primary text-sm px-4 py-2"
                >
                  Accept All
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <h2 className="font-heading font-bold text-neutral-900 mb-4">
              Cookie Preferences
            </h2>
            <p className="text-sm text-neutral-600 mb-4">
              Choose which cookies you allow. You can change these settings
              at any time from our{' '}
              <Link
                to="/cookies"
                className="text-primary-500 hover:text-primary-600 underline"
              >
                Cookie Policy
              </Link>{' '}
              page.
            </p>

            <div className="space-y-3 mb-6">
              {/* Necessary — always on */}
              <label className="flex items-center justify-between p-3 bg-neutral-50 rounded-xl">
                <div>
                  <span className="text-sm font-semibold text-neutral-900">
                    Necessary
                  </span>
                  <p className="text-xs text-neutral-500">
                    Authentication, security, core functionality, and
                    scrubbed error reports. Always active.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked
                  disabled
                  className="w-5 h-5 text-primary-500 rounded"
                />
              </label>

              {/* Functional */}
              <label className="flex items-center justify-between p-3 bg-neutral-50 rounded-xl cursor-pointer">
                <div>
                  <span className="text-sm font-semibold text-neutral-900">
                    Functional
                  </span>
                  <p className="text-xs text-neutral-500">
                    Language preferences and UI personalization.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={functional}
                  onChange={(e) => setFunctional(e.target.checked)}
                  className="w-5 h-5 text-primary-500 rounded cursor-pointer"
                />
              </label>

              {/* Analytics */}
              <label className="flex items-center justify-between p-3 bg-neutral-50 rounded-xl cursor-pointer">
                <div>
                  <span className="text-sm font-semibold text-neutral-900">
                    Analytics
                  </span>
                  <p className="text-xs text-neutral-500">
                    Anonymous usage statistics and browser performance
                    tracing to improve the platform.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="w-5 h-5 text-primary-500 rounded cursor-pointer"
                />
              </label>

              {/* Marketing */}
              <label className="flex items-center justify-between p-3 bg-neutral-50 rounded-xl cursor-pointer">
                <div>
                  <span className="text-sm font-semibold text-neutral-900">
                    Marketing
                  </span>
                  <p className="text-xs text-neutral-500">
                    Personalized ads and retargeting across other sites.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="w-5 h-5 text-primary-500 rounded cursor-pointer"
                />
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                onClick={rejectAll}
                className="btn-ghost text-sm px-4 py-2"
              >
                Reject All
              </button>
              <button
                onClick={handleSaveCustom}
                className="btn-primary text-sm px-4 py-2"
              >
                Save Preferences
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
